/**
 * Shared demo framework — oRPC client interception.
 *
 * The most thorough way to make the REAL pages render fixtures: wrap the oRPC
 * client link so that, while demo mode is on, EVERY registered demo's resolver
 * gets a chance to serve a procedure from fixtures (zero network), falling
 * through to the real link for everything none of them own. Because this sits
 * at the link layer it intercepts EVERY call style — wrapper hooks, `useQueries`
 * with raw `orpc.*.queryOptions`, and imperative `rpcClient.*` calls — which
 * per-hook shims cannot.
 *
 * Each demo registers a resolver keyed by its demo key (via `@/demo/init`). A
 * resolver receives the procedure `path` (e.g. `['accountsPayable','invoices','get']`)
 * and `input`, and returns the fixture, or `undefined` to fall through. Demos
 * own disjoint procedure domains (AP vs Workflows), so the first non-`undefined`
 * result wins.
 */

import type { ClientContext, ClientLink } from '@orpc/client';
import { isDemoMode } from './demo-mode';

/** Returns a fixture for `(path, input)`, or `undefined` to fall through. */
export type DemoProcedureResolver = (
  path: readonly string[],
  input: unknown,
) => unknown;

const resolvers = new Map<string, DemoProcedureResolver>();

/** Register (replace) the procedure resolver for a demo key. */
export function registerDemoClientResolver(
  demoKey: string,
  resolver: DemoProcedureResolver,
): void {
  resolvers.set(demoKey, resolver);
}

/**
 * Wrap the real oRPC client link so that, while demo mode is on, each demo's
 * resolver can short-circuit matched procedures with fixtures. A no-op when
 * demo mode is off or no resolver claims the procedure.
 */
export function createDemoClientLink<T extends ClientContext>(
  realLink: ClientLink<T>,
): ClientLink<T> {
  return {
    call(path, input, options) {
      if (isDemoMode()) {
        for (const resolver of resolvers.values()) {
          const result = resolver(path, input);
          if (result !== undefined) {
            return Promise.resolve(result);
          }
        }
      }
      return realLink.call(path, input, options);
    },
  };
}
